import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  aIso, desdeIso, esFinDeSemana, esElegible, formatearFechaLimite,
  hoy, mismoDia, rangoElegible,
} from '../../utils/fechaLimite';
import usePopoverAnclado from '../../hooks/usePopoverAnclado';
import '../../css/selector-fecha.css';

/**
 * Campo de fecha con calendario propio, reutilizable.
 *
 * No se usa `<input type="date">`: cada navegador pinta el suyo, no deja marcar
 * los días no elegibles ni explicar por qué, y el aspecto se sale del resto de
 * la aplicación. Aquí los días fuera de la ventana se ven pero no se pueden
 * pulsar, que es lo que hace entendible la restricción.
 *
 * Por defecto la ventana es la de la fecha de entrega de un ticket (desde 10
 * días hábiles vista, sin fines de semana y sin tope por arriba), pero se
 * puede pasar otra con `rango` para reaprovecharlo en otros formularios.
 *
 * @param value     fecha en AAAA-MM-DD (o cadena vacía)
 * @param onChange  recibe la fecha elegida en AAAA-MM-DD
 * @param rango     { desde, hasta } opcional (`hasta` en null = sin tope);
 *                  por defecto, el de la entrega
 * @param error     true para marcar el campo en rojo
 * @param permitirFinDeSemana  para filtros por fecha, donde el sábado y el
 *                  domingo son días válidos (a diferencia de una entrega)
 */
const SelectorFecha = ({ value, onChange, rango, error = false, id, placeholder, permitirFinDeSemana = false }) => {
  const { t, i18n } = useTranslation();
  // El calendario se pinta en el body: dentro de un panel con scroll o de una
  // tabla con overflow, un panel absoluto se recorta
  const { abierto, alternar, cerrar, refBoton, refPanel, estilo } = usePopoverAnclado({ ancho: 300 });

  const limites = useMemo(() => rango || rangoElegible(), [rango]);
  const seleccionada = useMemo(() => desdeIso(value), [value]);

  // El mes que se ve arranca en la fecha elegida o en el primer día disponible
  const [mesVisible, setMesVisible] = useState(() => {
    const base = desdeIso(value) || limites.desde;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  // Al abrirlo se vuelve al mes que toca: si el usuario navegó y cerró sin
  // elegir, reabrir en un mes vacío desorienta
  useEffect(() => {
    if (!abierto) return;
    const base = desdeIso(value) || limites.desde;
    setMesVisible(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [abierto, value, limites.desde]);

  const idioma = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';

  /** Semanas del mes visible, empezando en lunes y rellenando con los huecos. */
  const semanas = useMemo(() => {
    const primero = new Date(mesVisible.getFullYear(), mesVisible.getMonth(), 1);
    const ultimo = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + 1, 0);
    // getDay() da 0 para domingo: se recoloca para que la semana empiece en lunes
    const desplazamiento = (primero.getDay() + 6) % 7;

    const celdas = Array.from({ length: desplazamiento }, () => null);
    for (let dia = 1; dia <= ultimo.getDate(); dia += 1) {
      celdas.push(new Date(mesVisible.getFullYear(), mesVisible.getMonth(), dia));
    }
    while (celdas.length % 7 !== 0) celdas.push(null);

    return Array.from({ length: celdas.length / 7 }, (_, i) => celdas.slice(i * 7, i * 7 + 7));
  }, [mesVisible]);

  const nombresDias = useMemo(() => {
    // Un lunes cualquiera como semilla para sacar las iniciales en el idioma activo
    const lunes = new Date(2024, 0, 1);
    return Array.from({ length: 7 }, (_, i) => {
      const dia = new Date(lunes);
      dia.setDate(lunes.getDate() + i);
      return new Intl.DateTimeFormat(idioma, { weekday: 'short' }).format(dia).slice(0, 2);
    });
  }, [idioma]);

  const puedeRetroceder = mesVisible > new Date(limites.desde.getFullYear(), limites.desde.getMonth(), 1);
  const puedeAvanzar = !limites.hasta
    || mesVisible < new Date(limites.hasta.getFullYear(), limites.hasta.getMonth(), 1);

  const cambiarMes = (delta) => setMesVisible((mes) => new Date(mes.getFullYear(), mes.getMonth() + delta, 1));

  const puedeElegirse = (fecha) => (
    permitirFinDeSemana
      ? Boolean(fecha) && fecha >= limites.desde && (!limites.hasta || fecha <= limites.hasta)
      : esElegible(fecha, limites)
  );

  const elegir = (fecha) => {
    if (!puedeElegirse(fecha)) return;
    onChange(aIso(fecha));
    cerrar();
  };

  const motivoNoElegible = (fecha) => {
    if (!permitirFinDeSemana && esFinDeSemana(fecha)) return t('fechaLimite.finDeSemana');
    if (fecha < limites.desde) return t('fechaLimite.enElPasado');
    return t('fechaLimite.fueraDeVentana');
  };

  return (
    // `sf-campo--abierto` y no `abierto` a secas: esa clase es global (el badge
    // verde de estado de ticket) y le colaba al campo su fondo y su padding.
    <div className={`sf-campo ${abierto ? 'sf-campo--abierto' : ''} ${error ? 'sf-campo--error' : ''}`}>
      {/* Los atributos data-* le dicen a los gestores de contraseñas que este
          campo no es rellenable: si no, lo resaltan con un halo propio. */}
      <button
        type="button"
        id={id}
        ref={refBoton}
        className="sf-disparador"
        onClick={alternar}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        autoComplete="off"
        data-form-type="other"
        data-1p-ignore=""
        data-lpignore="true"
        data-bwignore="true"
      >
        <FiCalendar aria-hidden="true" />
        <span className={value ? 'sf-valor' : 'sf-placeholder'}>
          {value ? formatearFechaLimite(value) : (placeholder || t('fechaLimite.placeholder'))}
        </span>
      </button>

      {abierto && estilo && createPortal(
        <div ref={refPanel} className="sf-panel" style={estilo} role="dialog" aria-label={t('fechaLimite.calendario')}>
          <div className="sf-cabecera">
            <button
              type="button"
              onClick={() => cambiarMes(-1)}
              disabled={!puedeRetroceder}
              aria-label={t('fechaLimite.mesAnterior')}
            >
              <FiChevronLeft />
            </button>
            <strong>
              {new Intl.DateTimeFormat(idioma, { month: 'long', year: 'numeric' }).format(mesVisible)}
            </strong>
            <button
              type="button"
              onClick={() => cambiarMes(1)}
              disabled={!puedeAvanzar}
              aria-label={t('fechaLimite.mesSiguiente')}
            >
              <FiChevronRight />
            </button>
          </div>

          <div className="sf-dias-semana" aria-hidden="true">
            {nombresDias.map((dia, i) => <span key={i}>{dia}</span>)}
          </div>

          <div className="sf-rejilla">
            {semanas.map((semana, indiceSemana) => semana.map((dia, indiceDia) => {
              if (!dia) return <span key={`${indiceSemana}-${indiceDia}`} className="sf-hueco" />;

              const elegible = puedeElegirse(dia);
              const clases = [
                'sf-dia',
                elegible ? '' : 'no-elegible',
                esFinDeSemana(dia) ? 'finde' : '',
                mismoDia(dia, seleccionada) ? 'elegida' : '',
                mismoDia(dia, hoy()) ? 'hoy' : '',
              ].filter(Boolean).join(' ');

              return (
                <button
                  type="button"
                  key={`${indiceSemana}-${indiceDia}`}
                  className={clases}
                  onClick={() => elegir(dia)}
                  disabled={!elegible}
                  title={elegible ? '' : motivoNoElegible(dia)}
                  aria-current={mismoDia(dia, hoy()) ? 'date' : undefined}
                >
                  {dia.getDate()}
                </button>
              );
            }))}
          </div>

          <p className="sf-ayuda">
            {limites.hasta
              ? t('fechaLimite.ventana', {
                desde: formatearFechaLimite(aIso(limites.desde)),
                hasta: formatearFechaLimite(aIso(limites.hasta)),
              })
              : t('fechaLimite.desdeMinimo', {
                desde: formatearFechaLimite(aIso(limites.desde)),
              })}
          </p>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SelectorFecha;
